import React, { useEffect, useId, useMemo, useState } from 'react';
import styled from 'styled-components';
import { CrudList, CrudStyles } from '../../shared/Crud';
import costCenterService from '../../../services/costCenterService';
import { COST_CENTER_LIST_COLUMNS } from '../../CostCenters/costCenterListColumns';

const { FormGroup, Input, Button } = CrudStyles;

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    box-sizing: border-box;
    padding: 16px;
    overflow-y: auto;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: ${({ $zIndex }) => $zIndex ?? 9999};
`;

const ModalBox = styled.div`
    box-sizing: border-box;
    background: #fff;
    padding: 24px;
    border-radius: 12px;
    width: 100%;
    max-width: min(1100px, calc(100vw - 32px));
    max-height: min(90vh, 90dvh);
    overflow: hidden;
    min-width: 0;
    display: flex;
    flex-direction: column;
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    flex-shrink: 0;

    h2 {
        margin: 0;
        font-size: 18px;
        color: ${({ theme }) => theme?.colors?.gray?.[900] || '#222'};
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

const CloseBtn = styled.button`
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    color: #666;
    font-size: 24px;
    line-height: 1;
    &:hover {
        color: #222;
    }
`;

const FiltersRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: flex-end;
`;

const fieldStyle = { flex: '1 1 240px', minWidth: 0 };

const FILTER_DEBOUNCE_MS = 500;

export default function DeliveryCostCenterSelectorModal({
    open,
    customerId,
    currentCostCenterId,
    onClose,
    onSelectCostCenter,
    onRemoveCostCenter,
    isSubmitting = false,
    removalConfirmMessage = 'Deseja remover o centro de custo desta entrega?',
    removeButtonTitle = 'Remover centro de custo da entrega',
    zIndex = 9999,
}) {
    const idPrefix = useId();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);

    const [localName, setLocalName] = useState('');
    const [localCode, setLocalCode] = useState('');
    const [searchName, setSearchName] = useState('');
    const [searchCode, setSearchCode] = useState('');

    const [pagination, setPagination] = useState({
        page: 0,
        totalPages: 0,
        first: true,
        last: true,
    });

    useEffect(() => {
        if (!open) return;
        setLocalName('');
        setLocalCode('');
        setSearchName('');
        setSearchCode('');
        setData([]);
        setPagination({ page: 0, totalPages: 0, first: true, last: true });
        setLoadError(null);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const t = setTimeout(() => {
            setSearchName(localName);
            setSearchCode(localCode);
            setPagination((p) => ({ ...p, page: 0 }));
        }, FILTER_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [localName, localCode, open]);

    useEffect(() => {
        if (!open) return;
        if (customerId == null || String(customerId).trim() === '') {
            setData([]);
            setLoading(false);
            setLoadError('Selecione um cliente para listar centros de custo.');
            return;
        }

        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setLoadError(null);
            try {
                const result = await costCenterService.list(customerId, {
                    page: pagination.page,
                    size: 10,
                    sort: 'name,asc',
                    active: true,
                    ...(searchName.trim() ? { name: searchName.trim() } : {}),
                    ...(searchCode.trim() ? { code: searchCode.trim() } : {}),
                });

                if (cancelled) return;

                const content = result.content || [];
                const totalPages =
                    result.page?.totalPages != null && Number.isFinite(Number(result.page.totalPages))
                        ? Number(result.page.totalPages)
                        : content.length === 0
                          ? 0
                          : 1;

                const pageNumber = result.page?.number != null && Number.isFinite(Number(result.page.number)) ? Number(result.page.number) : pagination.page;
                setData(content);
                setPagination({
                    page: pageNumber,
                    totalPages,
                    first: totalPages === 0 || pageNumber === 0,
                    last: totalPages === 0 || pageNumber >= totalPages - 1,
                });
            } catch (e) {
                if (!cancelled) {
                    setLoadError('Não foi possível carregar os centros de custo.');
                    setData([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [open, customerId, pagination.page, searchName, searchCode]);

    const hasFilters = useMemo(() => Boolean(localName.trim() || localCode.trim()), [localName, localCode]);

    const handleClearFilters = () => {
        setLocalName('');
        setLocalCode('');
        setSearchName('');
        setSearchCode('');
        setPagination((p) => ({ ...p, page: 0 }));
    };

    const handlePageChange = (newPage) => {
        setPagination((p) => ({ ...p, page: newPage }));
    };

    const renderActions = (item) => {
        const isCurrent = currentCostCenterId != null && String(currentCostCenterId) === String(item?.id);
        return (
            <Button
                type="button"
                variant="primary"
                disabled={isSubmitting || isCurrent}
                onClick={() => onSelectCostCenter?.(item)}
                title={isCurrent ? 'Este é o centro de custo atual' : 'Selecionar centro de custo'}
                style={{ justifyContent: 'center' }}
            >
                {isSubmitting ? 'Salvando...' : isCurrent ? 'Atual' : 'Selecionar'}
            </Button>
        );
    };

    if (!open) return null;

    return (
        <Overlay
            $zIndex={zIndex}
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.target === e.currentTarget && onClose?.()}
        >
            <ModalBox onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <h2>Selecionar centro de custo</h2>
                    <CloseBtn type="button" onClick={onClose} title="Fechar">
                        ×
                    </CloseBtn>
                </ModalHeader>

                <CrudList
                    hideHeader
                    title="Centros de custo"
                    tableMinWidth="680px"
                    columns={COST_CENTER_LIST_COLUMNS}
                    data={data}
                    loading={loading}
                    actions={renderActions}
                    pagination={{
                        ...pagination,
                        onPageChange: handlePageChange,
                    }}
                    filters={
                        <>
                            <FiltersRow>
                                <FormGroup style={fieldStyle}>
                                    <label htmlFor={`${idPrefix}-name`}>Nome</label>
                                    <Input
                                        id={`${idPrefix}-name`}
                                        type="text"
                                        value={localName}
                                        onChange={(e) => setLocalName(e.target.value)}
                                        placeholder="Filtrar por nome"
                                        autoComplete="off"
                                    />
                                </FormGroup>
                                <FormGroup style={fieldStyle}>
                                    <label htmlFor={`${idPrefix}-code`}>Código</label>
                                    <Input
                                        id={`${idPrefix}-code`}
                                        type="text"
                                        value={localCode}
                                        onChange={(e) => setLocalCode(e.target.value)}
                                        placeholder="Filtrar por código"
                                        autoComplete="off"
                                    />
                                </FormGroup>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleClearFilters}
                                    disabled={!hasFilters}
                                    title={hasFilters ? 'Limpar filtros' : 'Nenhum filtro aplicado'}
                                >
                                    Limpar
                                </Button>
                            </FiltersRow>
                            {loadError && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }} role="alert">
                                    <p style={{ margin: 0, color: '#e74c3c', fontSize: 14 }}>{loadError}</p>
                                </div>
                            )}
                        </>
                    }
                />

                {currentCostCenterId != null && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 12 }}>
                        <Button
                            type="button"
                            variant="danger"
                            disabled={isSubmitting}
                            onClick={() => {
                                const ok = window.confirm(removalConfirmMessage);
                                if (!ok) return;
                                onRemoveCostCenter?.();
                            }}
                            title={removeButtonTitle}
                        >
                            Remover centro de custo
                        </Button>
                    </div>
                )}
            </ModalBox>
        </Overlay>
    );
}

